import {
  AspectRatio,
  AlignItems,
  BorderStyle,
  Body2,
  Body3,
  Bold,
  Button,
  ButtonColorVariant,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  GridDisplay,
  Heading2,
  Image,
  Join,
  JustifyContent,
  Space,
  TextAlign,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { type DashboardExperienceOrderResponse } from '@library/api-dashboard-experience'
import React from 'react'
import { browserHistory } from 'react-router'
import EmptyPlateJPG from '../../../my-gousto-page/src/assets/emptyPlate.png'
import { useOrderTracking } from '../hooks/useOrderTracking'

type OrderTileProps = {
  order: DashboardExperienceOrderResponse
}

const StyledImage = withCitrusTheme(Image, () => ({
  filter: 'drop-shadow(10px 17px 11px #00000021)',
  boxShadow:
    '1px 2px 5px 0px #0000001A, 4px 8px 9px 0px #00000017, 10px 17px 12px 0px #0000000D, 18px 31px 14px 0px #00000003, 28px 48px 16px 0px #00000000',
  borderRadius: '50%',
}))

const MAX_RECIPE_SLOTS = 5

const RecipePlaceholder = () => (
  <AspectRatio ratio={1 / 1}>
    <Box
      borderStyle={BorderStyle.Dashed}
      borderColor={Color.ColdGrey_200}
      borderWidth={0.5}
      width="100%"
      data-testid="empty-recipe-image"
      paddingBottom={[1, 3]}
      paddingH={[1, 3]}
      paddingRight={[1, 3]}
      paddingV={[1, 3]}
    >
      <StyledImage src={EmptyPlateJPG} alt="Recipe placeholder" />
    </Box>
  </AspectRatio>
)

const RecipeTile = ({ src }: { src: string }) => (
  <AspectRatio ratio={1 / 1}>
    <Image src={src} alt="Recipe Image" />
  </AspectRatio>
)

export function OrderTile({ order }: OrderTileProps) {
  const { trackGetHelpWithThisBoxClick } = useOrderTracking()
  const recipeSlots = Array.from(
    { length: MAX_RECIPE_SLOTS },
    (_, index) => order.recipes[index] ?? null,
  )
  const originalPrice = order.price.original
  const currentPrice = order.price.amount
  const handleGetHelpClick = () => {
    trackGetHelpWithThisBoxClick({
      orderId: order.id,
    })
    browserHistory.push(`/get-help?orderId=${order.id}`)
  }

  return (
    <Box
      data-testid="order-tile"
      width={'100%'}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      borderWidth={0.5}
      borderColor={Color.ColdGrey_100}
      borderStyle={BorderStyle.Solid}
      paddingV={4}
      paddingH={4}
      bg={Color.White}
      style={{ borderRadius: '8px' }}
    >
      <Join with={<Space size={4} direction="vertical" />}>
        <Box
          width="100%"
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          justifyContent={JustifyContent.SpaceBetween}
          alignItems={AlignItems.FlexStart}
        >
          <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
            <Heading2 size={[3, 5]}>{order.title}</Heading2>
            <Space size={1} direction="vertical" />
            <Box
              display={Display.Flex}
              flexDirection={FlexDirection.Row}
              alignItems={AlignItems.Center}
              gap={3}
            >
              <Body2 color={Color.ColdGrey_600} fontWeight={FontWeight.Bold}>
                {order.state_title}
              </Body2>
              <Body3 color={Color.ColdGrey_800}>|</Body3>
              <Body3 color={Color.ColdGrey_800}>{order.summary_text}</Body3>
            </Box>
          </Box>
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            alignItems={AlignItems.FlexEnd}
          >
            {originalPrice ? (
              <Text
                size={[0, 1]}
                display={Display.Inline}
                textAlign={TextAlign.Right}
                color={Color.ColdGrey_600}
              >
                <s>{originalPrice}</s>
              </Text>
            ) : null}
            <Space size={[0, 2]} direction="vertical" />
            {currentPrice ? (
              <Text
                size={[4, 9]}
                display={Display.Inline}
                textAlign={TextAlign.Right}
                color={Color.ColdGrey_800}
              >
                <Bold>{currentPrice}</Bold>
              </Text>
            ) : null}
          </Box>
        </Box>
        <Box
          width={['100%', '100%', '75%']}
          display={GridDisplay.Grid}
          gridTemplateColumns={`repeat(${MAX_RECIPE_SLOTS}, 1fr)`}
          gap={[2, 4]}
        >
          {recipeSlots.map((recipe, index) => (
            <Box key={`${order.id}-recipe-slot-${index}`} width="100%">
              {recipe?.image_url ? <RecipeTile src={recipe.image_url} /> : <RecipePlaceholder />}
            </Box>
          ))}
        </Box>
        {order.help_cta ? (
          <Box display={Display.Flex}>
            <Button
              colorVariant={ButtonColorVariant.Secondary}
              onClick={handleGetHelpClick}
              width={['100%', '100%', '50%']}
            >
              {order.help_cta}
            </Button>
          </Box>
        ) : null}
      </Join>
    </Box>
  )
}
